/**
 * InitiativesModule - Initiatives Management Module
 * 
 * REFACTORED: Now uses unified panel system for consistent UI
 * 
 * Provides UI for initiative management:
 * - Create/Edit forms
 * - Initiative list with filters
 * - Transfer from chat handling
 * - Statistics dashboard
 * 
 * @module modules/initiatives/InitiativesModule
 */

import AppState from '../../core/AppState.js';
import EventBus, { AppEvents } from '../../core/EventBus.js';
import Config from '../../core/Config.js';
import InitiativeService from '../../services/InitiativeService.js';
import container from '../../core/ServiceContainer.js';
import { SERVICE_TOKENS as T } from '../../core/services.tokens.js';
import ValidationService from '../../services/ValidationService.js';
import { bindPromptEditors } from '../../components/PromptBinder.js';
import { mountInitiatives } from '../../ui/initiatives/mountInitiatives.jsx';
import { bindSafeClick } from '../../utils/SafeClick.js';
import * as ActionRegistry from '../../core/ActionRegistry.js';
import { confirmOverlay } from '../../utils/Overlay.js';
import { createLayoutPanel, createPanelActions, PANEL_VARIANTS } from '../../utils/panel.js';
import { icon as devIcon } from '../../ui/icons.js';

export class InitiativesModule {
  constructor() {
    this.container = null;
    this.currentView = 'list'; // 'list', 'form', 'detail', 'define'
    this.editingId = null;
    this.filters = {
      status: '',
      owner: '',
      priority: '',
      search: ''
    };
    this.sortBy = 'createdAt';
    this.sortOrder = 'desc';
  }
  
  /**
   * Initialize the module
   */
  async initialize() {
    // Resolve via DI (fallback to direct instance)
    this._svc = container.get(T.INITIATIVES) || InitiativeService;
    if (!this._svc.initialized) {
      await this._svc.initialize();
    }
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Subscribe to state changes
    AppState.subscribe('initiatives', () => {
      if (this.container) {
        this.render();
      }
    });
    
    // Also subscribe to transferredInitiative changes
    AppState.subscribe('transferredInitiative', (initiative) => {
      if (this.container && this.currentView === 'define' && initiative) {
        console.log('🔄 Transferred initiative detected, re-rendering...');
        this.render();
      }
    });
    
    console.log('🎯 Initiatives module initialized');
  }
  
  /**
   * Set up event listeners
   */
  setupEventListeners() {
    // Listen for populate event from chat
    EventBus.on(AppEvents.INITIATIVE_POPULATE, (initiative) => {
      this.populateForm(initiative);
      // Show CTA toast to review extractor results
      try {
        const id = window.Toast?.show('AI prepared suggestions. Review?', 'info');
        if (id) {
          const hook = () => { try { this.openAIExtractor(); } catch(_) {} };
          // Wait a tick to ensure the element exists
          setTimeout(() => {
            const el = document.querySelector(`.toast[data-id="${id}"]`);
            if (el) el.addEventListener('click', hook, { once: true });
          }, 0);
        }
      } catch (_) {}
    });
    
    // Listen for transfer from chat (legacy)
    EventBus.on(AppEvents.INITIATIVE_TRANSFER, (initiative) => {
      // Store transferred initiative in AppState
      AppState.setState({ transferredInitiative: initiative });
      
      // If we're in define view, refresh to show the new data
      if (this.currentView === 'define') {
        this.render();
      }
      
      // Show success message
      EventBus.emit(AppEvents.TOAST_SHOW, { 
        message: 'Initiative transferred! Complete the details and save.', 
        type: 'success' 
      });
    });
    
    // Listen for initiative events
    EventBus.on(AppEvents.INITIATIVE_ADDED, () => {
      // Clear transferred initiative after successful save
      AppState.setState({ transferredInitiative: null });
      this.render();
    });
    
    EventBus.on(AppEvents.INITIATIVE_UPDATED, () => {
      this.render();
    });
    
    EventBus.on(AppEvents.INITIATIVE_DELETED, () => {
      this.currentView = 'list';
      this.render();
    });
  }
  
  /**
   * Activate the module
   */
  activate() {
    console.log('🎯 Initiatives module activated');
    
    // Check which tab we're on to determine behavior
    const currentTab = AppState.getState('currentTab');
    
    if (currentTab === 'objectives') {
      // In objectives tab: show Define Initiative form
      this.currentView = 'define';
    } else {
      // In other tabs: show list view (Portfolio Analysis, etc.)
      this.currentView = 'list';
    }
    
    this.render();
  }
  
  /**
   * Mount the module to a container
   */
  mount(container) {
    this.container = container;
    this.render();
  }
  
  /**
   * Main render method
   */
  render() {
    if (!this.container) return;
    
    switch (this.currentView) {
      case 'form':
        this.renderForm();
        break;
      case 'detail':
        this.renderDetail();
        break;
      case 'define':
        this.renderDefineForm();
        break;
      case 'list':
      default:
        this.renderList();
        break;
    }
  }
  
  /**
   * Render Define Initiative form (for Objectives tab)
   */
  renderDefineForm() {
    // Check if there's a transferred initiative to work with
    const transferredInitiative = AppState.getState('transferredInitiative');
    const initiative = transferredInitiative || null;
    
    // Invite block visibility (when stakeholders exist on transferred data)
    const inviteBlock = (() => {
      const hasStakeholders = !!(initiative && Array.isArray(initiative?.stakeholders) && initiative.stakeholders.length);
      if (!hasStakeholders) return '';
      return `
        <div class="transfer-notice" id="inviteShare">
          <div class="notice-icon">✉️</div>
          <div class="notice-content">
            <h4>Invite a collaborator</h4>
            <p>We can email your initiative to someone who should weigh in.</p>
            <div style="display:flex;gap:8px;align-items:center;margin-top:8px">
              <input type="email" id="inviteEmail" class="form-control" placeholder="name@example.com" />
              <button type="button" class="btn btn-secondary btn-small" id="inviteSend">Invite</button>
            </div>
          </div>
        </div>`;
    })();

    // Build form content
    const formContent = `
      ${transferredInitiative ? `
        <div class="transfer-notice">
          <div class="transfer-icon"></div>
          <div class="transfer-content">
            <h4>Initiative Transferred from AI Chat</h4>
            <p>Review and complete the details below before saving to your portfolio.</p>
          </div>
          <button class="btn-ghost btn-small" data-action="clear-transfer">Start Fresh</button>
        </div>
      ` : `
        <div class="no-transfer-notice">
          <div class="notice-icon"></div>
          <div class="notice-content">
            <h4>No Initiative Ready</h4>
            <p>Chat with the AI assistant on the left to develop an initiative, then transfer it here to finalize.</p>
          </div>
        </div>
      `}
      ${inviteBlock}
      
      <form id="defineInitiativeForm" class="define-form" data-schema="initiative">
        <!-- AI Extractor removed: rely on chat transfer only -->
        <!-- Title -->
        <div class="form-group">
          <label for="define-title">Entry Title *</label>
          <input type="text" 
                 id="define-title" 
                 name="title" 
                 class="form-control"
                 value="${initiative?.title || ''}"
                 placeholder="e.g., Reduce customer churn through enhanced retention program"
                 required>
          <span class="error-message" data-error-for="title"></span>
        </div>
        
        <!-- Objective -->
        <div class="form-group">
          <label for="define-objective">Business Objective *</label>
          <textarea id="define-objective" 
                    name="objective" 
                    class="form-control"
                    rows="2"
                    placeholder="What business outcome will this achieve?"
                    required>${initiative?.objective || ''}</textarea>
          <span class="error-message" data-error-for="objective"></span>
        </div>
        
        <!-- Deliverable / Output (what will be built/done) -->
        <div class="form-group">
          <label for="define-deliverable">Deliverable / Output</label>
          <input type="text" 
                 id="define-deliverable" 
                 name="deliverable" 
                 class="form-control"
                 value="${initiative?.deliverable || ''}"
                 placeholder="e.g., New onboarding checklist; churn reduction playbook; self‑serve setup flow">
        </div>
        
        <!-- Key Results -->
        <div class="form-group">
          <label>Impact / Key Result / Outcome *</label>
          <div id="defineKeyResultsContainer">
            ${initiative?.keyResults ? initiative.keyResults.map((kr, i) => `
              <div class="key-result-input">
                <input type="text" 
                       name="keyResult_${i}" 
                       class="form-control key-result"
                       value="${kr}"
                       placeholder="Measurable key result...">
              </div>
            `).join('') : `
              <div class="key-result-input">
                <input type="text" 
                       name="keyResult_0" 
                       class="form-control key-result"
                       placeholder="Measurable key result...">
              </div>
            `}
          </div>
          <!-- Add KR button removed; edits flow from chat transfer -->
          <span class="error-message" data-error-for="keyResults"></span>
        </div>

        <!-- End-users / Stakeholders -->
        <div class="form-group">
          <label>Actor / Stakeholder / End-user</label>
          <div id="defineEndUsersContainer">
            ${Array.isArray(initiative?.stakeholders) && initiative.stakeholders.length
              ? initiative.stakeholders.slice(0,10).map((n, idx) => `
                <div class=\"end-user-input\">
                  <input type=\"text\" name=\"endUser_${idx}\" class=\"form-control end-user\" value=\"${String(n)}\" placeholder=\"e.g., Product Managers, New Customers\" />
                </div>
              `).join('')
              : `
                <div class=\"end-user-input\">
                  <input type=\"text\" name=\"endUser_0\" class=\"form-control end-user\" placeholder=\"e.g., Product Managers, New Customers\" />
                </div>
              `}
          </div>
          <!-- Add End-user button removed; edits flow from chat transfer -->
          <span class="text-muted">Optional; stored as Stakeholders for Impact Mapping.</span>
        </div>
        
        <!-- Metadata Row -->
        <div class="form-row">
          <!-- Owner -->
          <div class="form-group">
            <label for="define-owner">Owner *</label>
            <input type="text" 
                   id="define-owner" 
                   name="owner" 
                   class="form-control"
                   value="${initiative?.owner || ''}"
                   placeholder="Initiative owner..."
                   required>
            <span class="error-message" data-error-for="owner"></span>
          </div>
          
          <!-- Priority -->
          <div class="form-group">
            <label for="define-priority">Priority *</label>
            <select id="define-priority" name="priority" class="form-control" required>
              <option value="">Select Priority</option>
              <option value="low" ${initiative?.priority === 'low' ? 'selected' : ''}>Low</option>
              <option value="medium" ${initiative?.priority === 'medium' ? 'selected' : ''}>Medium</option>
              <option value="high" ${initiative?.priority === 'high' ? 'selected' : ''}>High</option>
              <option value="critical" ${initiative?.priority === 'critical' ? 'selected' : ''}>Critical</option>
            </select>
            <span class="error-message" data-error-for="priority"></span>
          </div>
        </div>
        
        <!-- Dates removed: edit in Portfolio/View Details -->
        
        <!-- Strategic Alignment -->
        <div class="form-group">
          <label for="define-strategicAlignment">Strategic Alignment</label>
          <input type="text" 
                 id="define-strategicAlignment" 
                 name="strategicAlignment" 
                 class="form-control"
                 value="${initiative?.strategicAlignment || ''}"
                 placeholder="Which strategic pillar or goal does this support?">
        </div>
        
        <!-- Initiative Type removed per Impact/OKR simplification -->
        
        <!-- Form Actions -->
        <div class="define-actions">
          <button type="submit" class="btn btn-primary btn-small">Save to Portfolio</button>
          <button type="button" class="btn btn-ghost btn-small" data-action="reset-form">
            Reset Form
          </button>
        </div>

      </form>
    `;
    
    // Create panel using layout-safe panel for grid layout
    const definePanel = createLayoutPanel({
      title: 'Define Initiative',
      variant: PANEL_VARIANTS.DEFAULT,
      className: 'define-initiative-container',
      content: formContent
    });
    
    // Render panel directly as the split child (no extra wrapper)
    this.container.innerHTML = definePanel;
    
    this.attachDefineFormHandlers();
    // Invite block rendered conditionally above; nothing else to do here
    // AI Extractor button removed
    // Bind PromptEditor buttons within this module's DOM
    bindPromptEditors(this.container);
    // Register routed actions for buttons in this view
    this.registerActions();
  }
  
  registerActions() {
    const mapping = {
      'clear-transfer': () => { AppState.setState({ transferredInitiative: null }); this.render(); },
      'reset-form': () => { const form = this.container?.querySelector('#defineInitiativeForm'); form?.reset(); },
      // Add/Remove actions removed; edits flow from chat transfer only
      'new-initiative': () => { this.editingId = null; this.currentView = 'form'; this.render(); },
      'edit': ({ dataset }) => { this.editingId = dataset?.id; this.currentView = 'form'; this.render(); },
      'delete': ({ dataset }) => {
        const id = dataset?.id;
        const initiative = id ? InitiativeService.getInitiative(id) : null;
        if (!id || !initiative) return;
        confirmOverlay('Delete Initiative', `Delete "${initiative.title}"? This cannot be undone.`, async () => {
          try { await InitiativeService.deleteInitiative(id); } catch (_) {}
        }, () => {});
      },
      'cancel': () => { this.currentView = 'list'; this.editingId = null; this.render(); }
    };
    ActionRegistry.registerMap('initiatives', mapping);
  }

  /**
   * Open AI extractor review overlay (Phase 1: local heuristic)
   */
  async openAIExtractor() {
    try {
      const form = document.getElementById('defineInitiativeForm');
      const title = form?.querySelector('#define-title')?.value?.trim() || '';
      const objective = form?.querySelector('#define-objective')?.value?.trim() || '';
      const description = form?.querySelector('#define-description')?.value?.trim() || '';
      const owner = form?.querySelector('#define-owner')?.value?.trim() || '';
      const krEls = form?.querySelectorAll('.key-result') || [];
      const krs = Array.from(krEls).map(i => i.value.trim()).filter(Boolean);
      const euEls = form?.querySelectorAll('.end-user') || [];
      const endUsers = Array.from(euEls).map(i => i.value.trim()).filter(Boolean);
      // Call PromptService InitiativeExtractor (sidecar when available, heuristic fallback otherwise)
      const PromptSvc = (await import('../../services/PromptService.js')).default;
      const draft = await PromptSvc.runInitiativeExtractor({ title, objective, description, key_results: krs, owner, stakeholders: endUsers });

      const { ModalService } = await import('../../ui-kit/ModalService.jsx');
      const { h } = await import('preact');
      const { ImpactExtractorReview } = await import('../../components/impact/ImpactExtractorReview.jsx');
      const { render } = await import('preact');
      const root = document.createElement('div');
      const close = ModalService.open({ title: 'AI Extractor Review', content: root, presentation: 'sheet' });

      const onCommit = async (accepted) => {
        try {
          const svcMod = await import('../../services/CausalGraphService.js');
          const svc = svcMod.default;
          const created = new Map();
          const mapped = accepted.mapTo || {};
          // Create nodes
          for (let i=0; i<(accepted.objectives||[]).length; i++) {
            const o = accepted.objectives[i];
            const mappedId = mapped.objectives?.[i];
            const id = mappedId || (await svc.createObjective({ title: o.title, description: o.description, owner })).id;
            created.set(o.title, id);
          }
          for (let i=0; i<(accepted.key_results||[]).length; i++) {
            const kr = accepted.key_results[i];
            const mappedId = mapped.key_results?.[i];
            const id = mappedId || (await svc.createKeyResult({ title: kr.title })).id;
            created.set(kr.title, id);
          }
          for (let i=0; i<(accepted.stakeholders||[]).length; i++) {
            const st = accepted.stakeholders[i];
            const mappedId = mapped.stakeholders?.[i];
            const id = mappedId || (await svc.createStakeholder({ name: st.name || st.title || 'Stakeholder' })).id;
            created.set(st.name || st.title, id);
          }
          for (let i=0; i<(accepted.impacts||[]).length; i++) {
            const im = accepted.impacts[i];
            const mappedId = mapped.impacts?.[i];
            const id = mappedId || (await svc.createBehavior({ title: im.title })).id;
            created.set(im.title, id);
          }
          for (let i=0; i<(accepted.deliverables||[]).length; i++) {
            const d = accepted.deliverables[i];
            const mappedId = mapped.deliverables?.[i];
            const id = mappedId || (await svc.createDeliverable({ title: d.title })).id;
            created.set(d.title, id);
          }
          // Create edges (best-effort by title resolution)
          for (const l of accepted.links || []) {
            const from = created.get(l.from) || l.from; const to = created.get(l.to) || l.to; if (!from || !to) continue;
            await svc.createEdgeTyped(String(from), String(to), l.type || 'influences', { confidence: l.confidence || 0.5 });
          }
          try { const Bus = (await import('../../services/graph/CanvasBus.js')).default; Bus.refresh(); } catch(_) {}
        } catch (e) { console.error('Commit failed', e); }
        close();
      };
      const onCancel = () => close();

      render(h(ImpactExtractorReview, { draft, onCancel, onCommit }), root);
    } catch (e) {
      console.error('openAIExtractor failed', e);
    }
  }
  
  /**
   * Attach event handlers for define form
   */
  attachDefineFormHandlers() {
    const form = this.container.querySelector('#defineInitiativeForm');
    if (!form) return;
    
    // Form submission
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.handleDefineFormSubmit();
    });
    // Invite via mailto (Phase 1)
    const sendBtn = this.container.querySelector('#inviteSend');
    if (sendBtn) {
      sendBtn.addEventListener('click', () => {
        const email = this.container.querySelector('#inviteEmail')?.value?.trim();
        if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { alert('Please enter a valid email address'); return; }
        const summary = this.buildInitiativeSummaryFromForm(form);
        const subject = encodeURIComponent(`Take a look: ${summary.title}`);
        const body = encodeURIComponent(`${summary.summary}\n\nSee details and get the app: ${window.location.origin}\n`);
        window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
      });
    }
    // Other click actions are handled by ActionRouter (registered in registerActions)
  }
  
  /**
   * Handle define form submission
   */
  async handleDefineFormSubmit() {
    const form = document.getElementById('defineInitiativeForm');
    const formData = new FormData(form);
    
    // Collect key results (Impact / KR / Outcome)
    const keyResults = [];
    form.querySelectorAll('.key-result').forEach(input => {
      if (input.value.trim()) {
        keyResults.push(input.value.trim());
      }
    });
    
    // Collect stakeholders (end-users)
    const stakeholders = [];
    form.querySelectorAll('.end-user').forEach(input => {
      const v = (input.value || '').trim();
      if (v) stakeholders.push(v);
    });

    // Build initiative data
    const data = {
      title: formData.get('title'),
      // description intentionally not used for outcome-first flow
      deliverable: formData.get('deliverable') || '',
      objective: formData.get('objective'),
      keyResults,
      stakeholders,
      owner: formData.get('owner'),
      priority: formData.get('priority'),
      startDate: formData.get('startDate'),
      endDate: formData.get('endDate'),
      strategicAlignment: formData.get('strategicAlignment'),
      // deliverable will be captured later when we add UI; keep slot ready
    };
    
    try {
      // Basic validation
      if (!data.title || !data.objective || !data.owner || !data.priority) {
        EventBus.emit(AppEvents.TOAST_SHOW, { 
          message: 'Please fill in all required fields', 
          type: 'error' 
        });
        return;
      }
      
      if (keyResults.length === 0) {
        EventBus.emit(AppEvents.TOAST_SHOW, { 
          message: 'Please add at least one key result', 
          type: 'error' 
        });
        return;
      }
      
      // Save initiative (canvas derives nodes at render time; no creation here)
      const saved = await InitiativeService.createInitiative(data);
      
      EventBus.emit(AppEvents.TOAST_SHOW, { 
        message: '🎉 Initiative saved to portfolio successfully!', 
        type: 'success' 
      });
      
      // Clear the form and transferred data
      AppState.setState({ transferredInitiative: null });
      this.render();
      
    } catch (error) {
      console.error('Define form submission error:', error);
      EventBus.emit(AppEvents.TOAST_SHOW, { 
        message: 'Error: ' + error.message, 
        type: 'error' 
      });
    }
  }

  buildInitiativeSummaryFromForm(form) {
    const title = form.querySelector('[name="title"]')?.value?.trim() || 'New Initiative';
    const objective = form.querySelector('[name="objective"]')?.value?.trim() || '';
    const description = form.querySelector('[name="description"]')?.value?.trim() || '';
    const owner = form.querySelector('[name="owner"]')?.value?.trim() || '';
    const krs = Array.from(form.querySelectorAll('.key-result')).map(i=>i.value.trim()).filter(Boolean).slice(0,5);
    const stakeholders = Array.from(form.querySelectorAll('.end-user')).map(i=>i.value.trim()).filter(Boolean).slice(0,5);
    const lines = [
      objective ? `Objective: ${objective}` : '',
      description ? `Summary: ${description}` : '',
      krs.length ? `Key Results: ${krs.join('; ')}` : '',
      stakeholders.length ? `For: ${stakeholders.join(', ')}` : '',
      owner ? `Owner: ${owner}` : ''
    ].filter(Boolean);
    return { title, summary: lines.join('\n') };
  }
  
  /**
   * Render initiatives list (for Portfolio and other tabs)
   */
  renderList() {
    const stats = InitiativeService.getStatistics();
    const initiatives = InitiativeService.getInitiatives({
      ...this.filters,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder
    });
    
    const actions = createPanelActions([
      { label: 'New Initiative', action: 'new-initiative', variant: 'primary', size: 'small' }
    ]);
    const content = `
      <div class="initiatives-container">
        <div class="text-secondary">${stats.total} initiatives • ${stats.completed} completed • ${stats.atRisk} at risk</div>
        <div class="stats-grid" style="margin-top: var(--spacing-md)">
          <div class="stat-card">
            <div class="stat-value">${stats.avgProgress}%</div>
            <div class="stat-label">Avg Progress</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${stats.completionRate}%</div>
            <div class="stat-label">Completion Rate</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${stats.byStatus['in-progress'] || 0}</div>
            <div class="stat-label">In Progress</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${stats.byPriority['high'] || 0}</div>
            <div class="stat-label">High Priority</div>
          </div>
        </div>
        <div id="initiatives-table-island"></div>
      </div>
    `;
    const panel = createLayoutPanel({
      title: 'Strategic Initiatives',
      actions,
      variant: PANEL_VARIANTS.DEFAULT,
      content,
      className: 'initiatives-panel'
    });

    this.container.innerHTML = panel;

    // Mount the Preact island for list + filters
    const host = this.container.querySelector('#initiatives-table-island');
    if (host) {
      try { mountInitiatives(host); } catch (e) { console.warn('Initiatives island mount failed:', e); }
    }
    // Click actions handled by ActionRouter
  }
  
  /**
   * Render initiative form (for creating/editing in other contexts)
   */
  renderForm() {
    const initiative = this.editingId ? InitiativeService.getInitiative(this.editingId) : null;
    const isEdit = !!initiative;
    
    // Build form content
    const formContent = `
      <form id="initiativeForm" class="initiative-form" data-schema="initiative">
        <!-- Similar form as define, but simplified for direct creation -->
        <div class="form-group">
          <label for="title">Title *</label>
          <input type="text" 
                 id="title" 
                 name="title" 
                 class="form-control"
                 value="${initiative?.title || ''}"
                 placeholder="e.g., Improve Customer Retention"
                 required>
        </div>
        
        <div class="form-group">
          <label for="description">Description *</label>
          <textarea id="description" 
                    name="description" 
                    class="form-control"
                    rows="4"
                    placeholder="Detailed description of the initiative..."
                    required>${initiative?.description || ''}</textarea>
        </div>
        
        <div class="form-group">
          <label for="objective">Business Objective *</label>
          <input type="text" 
                 id="objective" 
                 name="objective" 
                 class="form-control"
                 value="${initiative?.objective || ''}"
                 placeholder="What business outcome will this achieve?"
                 required>
        </div>
        
        <div class="form-actions">
          <button type="submit" class="btn-primary">
            ${isEdit ? 'Update Initiative' : 'Create Initiative'}
          </button>
          <button type="button" class="btn-ghost" data-action="cancel">
            Cancel
          </button>
        </div>
      </form>
    `;
    
    // Build header actions
    const headerActions = createPanelActions([
      { label: 'Cancel', action: 'cancel', variant: 'ghost', size: 'small' }
    ]);
    
    // Create panel - using layout-safe as it may be in various contexts
    const formPanel = createLayoutPanel({
      title: isEdit ? 'Edit Initiative' : 'New Initiative',
      actions: headerActions,
      variant: PANEL_VARIANTS.DEFAULT,
      className: 'initiative-form-container',
      content: formContent
    });
    
    this.container.innerHTML = formPanel;
    this.attachFormHandlers();
  }
  
  /**
   * Attach form event handlers
   */
  attachFormHandlers() {
    const form = this.container.querySelector('#initiativeForm');
    if (!form) return;
    
    // Form submission
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.handleFormSubmit();
    });
    // Cancel handled via ActionRouter
  }
  
  /**
   * Handle form submission
   */
  async handleFormSubmit() {
    const form = document.getElementById('initiativeForm');
    const formData = new FormData(form);
    
    const data = {
      title: formData.get('title'),
      description: formData.get('description'),
      objective: formData.get('objective')
    };
    
    try {
      if (this.editingId) {
        await InitiativeService.updateInitiative(this.editingId, data);
        EventBus.emit(AppEvents.TOAST_SHOW, { 
          message: 'Initiative updated successfully', 
          type: 'success' 
        });
      } else {
        await InitiativeService.createInitiative(data);
        EventBus.emit(AppEvents.TOAST_SHOW, { 
          message: 'Initiative created successfully', 
          type: 'success' 
        });
      }
      
      this.currentView = 'list';
      this.editingId = null;
      this.render();
      
    } catch (error) {
      console.error('Form submission error:', error);
      EventBus.emit(AppEvents.TOAST_SHOW, { 
        message: 'Error: ' + error.message, 
        type: 'error' 
      });
    }
  }
  
  /**
   * Populate form with initiative data (without saving)
   */
  populateForm(initiative) {
    console.log('📝 Populating form with:', initiative);
    
    // FIRST: Store in AppState so render knows there's a transferred initiative
    AppState.setState({ transferredInitiative: initiative });
    
    // If not in define view, switch to it
    if (this.currentView !== 'define') {
      this.currentView = 'define';
      this.render();
      // After render, form is fresh, so return and let render handle population
      return;
    }
    
    // Map initiative fields to form field IDs
    const fieldMap = {
      title: ['goal-title', 'initiative-title', 'define-title'],
      description: ['goal-description', 'initiative-description', 'define-description'],
      objective: ['goal-objective', 'objective', 'define-objective'],
      strategicAlignment: ['strategic-alignment', 'define-strategicAlignment'],
      priority: ['priority', 'define-priority'],
      timeframe: ['timeframe', 'define-timeframe'],
      owner: ['owner', 'define-owner'],
      type: ['define-type']
    };
    
    // Populate each field
    for (const [key, ids] of Object.entries(fieldMap)) {
      const value = initiative[key];
      if (!value) continue;
      
      for (const id of ids) {
        const field = document.getElementById(id);
        if (field) {
          field.value = value;
          field.dispatchEvent(new Event('input', { bubbles: true }));
          
          // Visual feedback
          field.style.transition = 'background-color 0.3s';
          field.style.backgroundColor = '#f0fdf4';
          setTimeout(() => {
            field.style.backgroundColor = '';
          }, 2000);
        }
      }
    }
    
    // Handle key results specially
    if (initiative.keyResults && Array.isArray(initiative.keyResults)) {
      // Populate key results in the define form if present
      const krContainer = document.getElementById('defineKeyResultsContainer');
      if (krContainer && initiative.keyResults.length > 0) {
        krContainer.innerHTML = initiative.keyResults.map((kr, i) => `
          <div class="key-result-input">
            <input type="text" 
                   name="keyResult_${i}" 
                   class="form-control key-result"
                   value="${kr}"
                   placeholder="Measurable key result...">
          </div>
        `).join('');
      }
    }

    // Handle stakeholders (end-users) specially
    if (Array.isArray(initiative.stakeholders)) {
      const euContainer = document.getElementById('defineEndUsersContainer');
      if (euContainer) {
        const list = initiative.stakeholders.filter(Boolean).slice(0, 10);
        if (list.length) {
          euContainer.innerHTML = list.map((n, i) => `
            <div class=\"end-user-input\">
              <input type=\"text\" name=\"endUser_${i}\" class=\"form-control end-user\" value=\"${String(n)}\" placeholder=\"e.g., Product Managers, New Customers\" />
            </div>
          `).join('');
        }
      }
    }
    
    // Enable the save button since we now have a transferred initiative
    const saveBtn = document.querySelector('#defineInitiativeForm button[type="submit"]');
    if (saveBtn) {
      saveBtn.disabled = false;
      saveBtn.style.opacity = '1';
      saveBtn.style.cursor = 'pointer';
    }
    
    // Show success message
    this.showMessage('Form populated - review and save when ready', 'success');
    
    // Force a re-render after a short delay to ensure everything updates
    setTimeout(() => {
      if (this.currentView === 'define' && AppState.getState('transferredInitiative')) {
        this.render();
      }
    }, 100);
  }
  
  /**
   * Show message to user
   */
  showMessage(message, type = 'info') {
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#10b981' : type === 'error' ? '#ef4444' : '#3b82f6'};
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 8px;
      z-index: 10000;
      animation: slideIn 0.3s ease;
    `;
    document.body.appendChild(toast);
    
    setTimeout(() => toast.remove(), 3000);
  }
  
  /**
   * Render detail view (future enhancement)
   */
  renderDetail() {
    // Future: Detailed view of a single initiative
    this.renderList();
  }
}
